import React, { useContext, useEffect, useState } from 'react';
import { Modal } from '../Common/Modal';
import Alert from '../Common/Alert';
import visitService from '../../services/visitService';
import userService from '../../services/userService';
import { AuthContext } from '../../context/AuthContext';
import { VISIT_TYPES } from '../../utils/constants';
import { visits as visitMessages, common as commonMessages } from '../../utils/validationMessages';
import VisitModalHeader from './VisitModalHeader';
import './ConfirmPIModal.css';

const toLocalYmd = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const ConfirmFollowUpPIModal = ({ isOpen, onClose, visit, resolvedPiName, onConfirmed }) => {
  const { user } = useContext(AuthContext);
  const [confirmationMode, setConfirmationMode] = useState('');
  const [confirmedOn, setConfirmedOn] = useState('');
  const [confirmationSentTo, setConfirmationSentTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [pmUsers, setPmUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [visitDetail, setVisitDetail] = useState(null);
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (isOpen && visit) {
      const defaultDate = toLocalYmd();
      setConfirmationMode('');
      setConfirmedOn(defaultDate);
      setConfirmationSentTo('');
      setFieldErrors({});
    } else if (!isOpen) {
      setConfirmationMode('');
      setConfirmedOn('');
      setConfirmationSentTo('');
      setSaving(false);
      setPmUsers([]);
      setLoadingUsers(false);
      setVisitDetail(null);
      setFieldErrors({});
      setToast(null);
    }
  }, [isOpen, visit]);

  useEffect(() => {
    if (!isOpen || !visit) {
      setVisitDetail(null);
      return;
    }
    const visitId = visit.scheduledVisitID ?? visit.scheduledVisitId;
    if (!visitId) return;
    let cancelled = false;
    visitService.getById(visitId).then((detail) => {
      if (!cancelled) setVisitDetail(detail);
    }).catch(() => {
      if (!cancelled) setVisitDetail(null);
    });
    return () => { cancelled = true; };
  }, [isOpen, visit]);

  useEffect(() => {
    const loadPmUsers = async () => {
      if (!isOpen || !visit) return;
      const projectCode =
        (visit.projectCode ?? visit.ProjectCode ?? visit.studyCode ?? visit.StudyCode ?? '').trim();
      const siteNo = visit.siteID ?? visit.SiteID ?? visit.siteId ?? visit.siteNo ?? visit.SiteNo;
      if (!projectCode) {
        setPmUsers([]);
        return;
      }
      try {
        setLoadingUsers(true);
        const pmRole = await userService.resolvePmRoleForVisitLane(visit, user);
        const users = await userService.getByRole(pmRole, projectCode, siteNo);
        setPmUsers(users || []);
      } catch {
        setPmUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadPmUsers();
  }, [isOpen, visit, user]);

  if (!visit) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Confirm Letter Action Taken (Follow-Up)">
        <p>No visit selected.</p>
      </Modal>
    );
  }

  const validateForm = () => {
    const errs = {};
    const v = visitDetail ?? visit;
    if (!(confirmationMode || '').trim()) errs.confirmationMode = visitMessages.confirmationModeRequired;
    if (!(confirmedOn || '').trim()) errs.confirmedOn = visitMessages.confirmedOnRequired;
    else {
      const todayYmd = toLocalYmd();
      if (confirmedOn < todayYmd) {
        errs.confirmedOn = visitMessages.confirmedOnNotPast;
      } else {
        const chosen = new Date(confirmedOn);
        chosen.setHours(0, 0, 0, 0);
        if (v?.followUpLetterDate) {
          const letterDate = new Date(v.followUpLetterDate);
          letterDate.setHours(0, 0, 0, 0);
          if (chosen < letterDate) errs.confirmedOn = 'Confirmation Sent On cannot be before the Follow-Up Letter date.';
        }
      }
    }
    if (!(confirmationSentTo || '').trim()) errs.confirmationSentTo = visitMessages.confirmationRecipientRequired;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!visit.scheduledVisitID) return;
    setFieldErrors({});
    if (!validateForm()) return;
    void runAfterESign();
  };

  const runAfterESign = async () => {
    setSaving(true);
    try {
      const confirmedOnVal = (confirmedOn || '').trim() ? confirmedOn : null;
      const confirmationSentToVal = (confirmationSentTo || '').trim() ? Number(confirmationSentTo) : null;
      const confirmationModeVal = (confirmationMode || '').trim() || null;
      const remarkVal = '';
      await visitService.followUpConfirm(visit.scheduledVisitID, {
        confirmationMode: confirmationModeVal,
        confirmedOn: confirmedOnVal,
        confirmationSentToUserID: confirmationSentToVal,
        remark: remarkVal || undefined,
      });
      if (onConfirmed) onConfirmed();
      setToast({ type: 'success', message: commonMessages.authSuccess });
      onClose();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to confirm follow-up letter action taken.';
      setToast({ type: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const visitTypeLabel = VISIT_TYPES[visit.visitType] || visit.visitType || '';
  const confirmedOnMinYmd = toLocalYmd();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
      title="Confirm Letter Action Taken (Follow-Up)"
      className="confirm-pi-modal"
    >
      <form onSubmit={handleSubmit}>
        <VisitModalHeader
          projectCode={visit.projectCode}
          siteName={visit.siteName}
          siteCode={visit.siteCode ?? visit.SiteCode}
          piName={resolvedPiName ?? visit.piName ?? visit.PIName ?? visit.pIName}
        />

        <div className="form-group">
          <label className="form-label">Visit Type</label>
          <input type="text" className="form-input" value={visitTypeLabel} disabled />
        </div>

        <div className="form-group">
          <label className="form-label">Confirmation Mode <span className="required-asterisk">*</span></label>
          <select className="form-select" value={confirmationMode} onChange={(e) => { setConfirmationMode(e.target.value); setFieldErrors(prev => ({ ...prev, confirmationMode: '' })); }}>
            <option value="">Select Confirmation Mode</option>
            <option value="Email">Email</option>
            <option value="Phone">Phone</option>
            <option value="Teams">Teams</option>
            <option value="Other">Other</option>
          </select>
          {fieldErrors.confirmationMode && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.confirmationMode}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">Confirmation Sent On <span className="required-asterisk">*</span></label>
          <input
            type="date"
            className="form-input"
            value={confirmedOn}
            min={confirmedOnMinYmd}
            onChange={(e) => { setConfirmedOn(e.target.value); setFieldErrors(prev => ({ ...prev, confirmedOn: '' })); }}
          />
          {fieldErrors.confirmedOn && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.confirmedOn}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">Confirmation Informed to PM <span className="required-asterisk">*</span></label>
          <select className="form-select" value={confirmationSentTo} onChange={(e) => { setConfirmationSentTo(e.target.value); setFieldErrors(prev => ({ ...prev, confirmationSentTo: '' })); }} disabled={loadingUsers || pmUsers.length === 0}>
            <option value="">
              {loadingUsers ? 'Loading PM users...' : pmUsers.length === 0 ? 'No PM users available' : 'Select Confirmation Informed to PM'}
            </option>
            {pmUsers.map((u) => (
              <option key={u.userId} value={u.userId}>{u.fullName ?? u.FullName ?? ''}</option>
            ))}
          </select>
          {fieldErrors.confirmationSentTo && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.confirmationSentTo}</div>}
        </div>

        <div className="confirm-pi-footer">
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Close</button>
        </div>
      </form>

      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
          <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}

    </Modal>
  );
};

export default ConfirmFollowUpPIModal;

